// ---------------------------------------------------------------------------
// V8's screens: role definitions, what each requires, and the gate check.
//
// DELIBERATELY MINIMAL, on the same instruction and the same merits as
// Credentials.tsx. docs/11-console-design-system.md replaces the console's
// direction before V10; this file introduces no colour token, no new component
// and no bespoke layout — only StatusChip, .data-table, .panel and .field — so
// the rework is a values change rather than a rewrite.
//
// WHAT IT DOES INVEST IN IS NOT VISUAL:
//
// 1. THE SHIFT END IS A REQUIRED FIELD WITH ITS OWN LABEL. 09-... §V8.2 is a
//    case a scheduler misses because nothing ever asks them the question. The
//    check panel asks it, in words, and will not run without it.
//
// 2. EVERY VERDICT RENDERS THE SERVER'S SENTENCE VERBATIM. Not a sentence this
//    file composes from a status code — that would be a second implementation
//    of the rule, and §V8.2 is about what happens when two implementations
//    disagree. gate.ts maps status to a tone and nothing else.
//
// 3. A WAIVER IS AMBER, NEVER TEAL. The credential is still not held. See
//    gate.ts for the argument; it is the one judgement here worth arguing about.
//
// 4. NO VOCABULARY IS WRITTEN HERE. Credential types, roles and waiver reasons
//    are all fetched.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useState } from "react";

import { may, send } from "../api/auth";
import {
  console_,
  type CredentialType,
  type RoleDef,
  type RoleRequirement as Requirement,
} from "../api/console";
import { StatusChip } from "../design/StatusChip";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import { typedTimeHint } from "../system/clock";
import { localDateTimeInput } from "../system/format";
import { conflictSummary, conflictTone, gateSummary, gateTone, type GateResult } from "../system/gate";
import { useSession } from "../session/SessionProvider";
import { usePartialLoad } from "./PartialLoad";
import { SearchSelect } from "./SearchSelect";

/**
 * Any response body, which may instead be an error envelope. `send` reports the
 * status and the parsed body and never throws, so every caller has to say what
 * it will do when the answer is a refusal — which is the shape that stopped V5
 * rendering an empty table where a 403 belonged.
 */
type Failable<T> = Partial<T> & { error?: { message?: string } };

type WorkerOption = { id: string; display_name: string };

export function RoleDefinitions() {
  const { apiBase, me } = useSession();
  const { t, refusal } = useT();
  const partial = usePartialLoad();
  const [roles, setRoles] = useState<RoleDef[]>([]);
  const [types, setTypes] = useState<CredentialType[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  // ADVISORY ONLY, and `may` says so in its own doc comment: this decides
  // whether to SHOW a control, never whether the act is allowed. The API
  // authorises, the policy refuses, and the trigger gates. A console that got
  // this wrong would offer a button that 403s, which is rude; a console that
  // relied on it would be a security control in a browser.
  const mayConfigure = me ? may(me, "config.manage") : false;

  const loadRoles = useCallback(async () => {
    // D88-40. This screen lists every role, the hidden ones with their switch.
    const { status, body } = await send<Failable<{ role_defs: RoleDef[] }>>(console_.listRoleDefs(apiBase, { includeHidden: true }));
    if (status === 200 && body.role_defs) setRoles(body.role_defs);
    else setError(refusal(body.error) ?? t("We could not load the roles."));
    setLoaded(true);
  }, [apiBase]);

  async function toggleHidden(id: string, hidden: boolean) {
    const { status, body } = await send<Failable<{ hidden: boolean }>>(console_.setRoleDefHidden(apiBase, id, hidden));
    if (status === 200) setRoles((rs) => rs.map((r) => (r.id === id ? { ...r, hidden } : r)));
    else setError(refusal(body.error) ?? t("We could not change that role."));
  }

  useEffect(() => {
    void loadRoles();
    void send<Failable<{ credential_types: CredentialType[] }>>(console_.listCredentialTypes(apiBase)).then(
      ({ status, body }) => {
        if (status === 200 && body.credential_types) setTypes(body.credential_types);
        else partial.note();
      },
    ).catch(() => partial.note());
  }, [apiBase, loadRoles]);

  useEffect(() => {
    if (!selected) {
      setRequirements([]);
      return;
    }
    void send<Failable<{ requirements: Requirement[] }>>(console_.listRoleRequirements(apiBase, selected)).then(
      ({ status, body }) => {
        if (status === 200 && body.requirements) setRequirements(body.requirements);
        else partial.note();
      },
    ).catch(() => partial.note());
  }, [apiBase, selected]);

  const refreshRequirements = useCallback(async () => {
    if (!selected) return;
    const { status, body } = await send<Failable<{ requirements: Requirement[] }>>(
      console_.listRoleRequirements(apiBase, selected),
    );
    if (status === 200 && body.requirements) setRequirements(body.requirements);
  }, [apiBase, selected]);

  return (
    <section className="panel">
      {partial.notice}
      <h1>{t("Roles")}</h1>
      <p className="muted">
        {t("What a person is being asked to do, and what that requires. A role's blocking credentials are checked against the end of every shift before anyone can be assigned to it.")}
      </p>

      {error ? <StatusChip tone="failed" label={error} /> : null}


      {(() => {
        // Tour T-46. The organisation's own roles first, then one collapsed group per ready-made
        // pack. A platform role that appears in several packs appears once in each, under its
        // pack, instead of as three identical rows in one flat table.
        const own = roles.filter((r) => !r.is_template);
        const packs = new Map<string, RoleDef[]>();
        for (const r of roles.filter((x) => x.is_template)) {
          const title = r.pack_display_name ?? r.pack_name ?? t("Platform template");
          packs.set(title, [...(packs.get(title) ?? []), r]);
        }
        // A workspace's own roles cannot be hidden, so their picker column would read "Yes" on every row.
        const rows = (list: RoleDef[], pickerColumn: boolean) => (
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">{t("Role")}</th>
                {pickerColumn ? <th scope="col">{t("Shown in pickers")}</th> : null}
              </tr>
            </thead>
            <tbody>
              {list.map((role) => (
                <tr key={role.id}>
                  <td>
                    <button type="button" className="link-button" onClick={() => setSelected(role.id)} aria-pressed={selected === role.id}>
                      {role.display_name ?? role.name}
                    </button>
                  </td>
                  {pickerColumn ? (
                    <td>
                      {/* D88-40. Only a system role is hidden; a workspace retires its own. */}
                      <input
                        type="checkbox"
                        aria-label={t("Show {role} in pickers", { role: role.display_name ?? role.name })}
                        checked={!role.hidden}
                        disabled={!mayConfigure}
                        onChange={(e) => void toggleHidden(role.id, !e.target.checked)}
                      />
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        );
        return (
          <div className="stack">
            <details className="role-group" open>
              <summary>
                {t("This organisation")} ({own.length})
              </summary>
              {own.length > 0 ? rows(own, false) : <p className="muted">{t("No roles are defined yet. A role names the work and lists the credentials it requires — without one, nothing can be rostered.")}</p>}
            </details>
            {[...packs.entries()].map(([title, list]) => (
              <details className="role-group" key={title}>
                <summary>
                  {title} ({list.length})
                </summary>
                {rows(list, true)}
              </details>
            ))}
          </div>
        );
      })()}

      {selected ? (
        <RequirementPanel
          roleDefId={selected}
          requirements={requirements}
          types={types}
          mayConfigure={mayConfigure}
          onChanged={refreshRequirements}
        />
      ) : null}
    </section>
  );
}

function RequirementPanel({
  roleDefId,
  requirements,
  types,
  mayConfigure,
  onChanged,
}: {
  roleDefId: string;
  requirements: Requirement[];
  types: CredentialType[];
  mayConfigure: boolean;
  onChanged: () => void;
}) {
  const { apiBase } = useSession();
  const { t, refusal } = useT();
  const [adding, setAdding] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function add() {
    if (!adding) return;
    const { status, body } = await send<Failable<unknown>>(
      console_.addRoleRequirement(apiBase, roleDefId, adding),
    );
    if (status === 200 || status === 201) {
      setAdding("");
      setError(null);
      onChanged();
    } else {
      setError(refusal(body.error) ?? t("That did not work."));
    }
  }

  async function remove(id: string) {
    const { status, body } = await send<Failable<unknown>>(
      console_.removeRoleRequirement(apiBase, roleDefId, id),
    );
    if (status === 200) {
      setError(null);
      onChanged();
    } else {
      setError(refusal(body.error) ?? t("That did not work."));
    }
  }

  return (
    <section className="panel">
      <h2>{t("What this role requires")}</h2>
      {error ? <StatusChip tone="failed" label={error} /> : null}
      {requirements.length === 0 ? (
        <p className="muted">
          {t("This role requires no credentials. Anyone can be assigned to it.")}
        </p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">{t("Credential")}</th>
              <th scope="col">{t("Effect")}</th>
              <th scope="col">{t("Source")}</th>
              <th scope="col"> </th>
            </tr>
          </thead>
          <tbody>
            {requirements.map((r) => (
              <tr key={r.id}>
                <td>{r.credential_name}</td>
                <td>
                  {/* Three distinct sentences, because they carry different
                      consequences: required by law and never waivable, required
                      by this organisation and waivable with a reason, or
                      advisory. Colour alone would not say which. */}
                  {r.is_statutory ? (
                    <StatusChip tone="failed" label={t("Required by law — never waivable")} />
                  ) : r.is_blocking ? (
                    <StatusChip tone="attention" label={t("Blocking — waivable with a reason")} />
                  ) : (
                    <StatusChip tone="neutral" label={t("Advisory")} />
                  )}
                </td>
                <td>{r.is_template ? t("Platform template") : t("This organisation")}</td>
                <td>
                  {mayConfigure && !r.is_template ? (
                    <button type="button" onClick={() => void remove(r.id)}>
                      {t("Remove")}
                    </button>
                  ) : (
                    <span className="muted">{r.is_template ? t("Set by the platform") : ""}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {mayConfigure ? (
        <div className="field">
          <label htmlFor="add-requirement">{t("Add a required credential")}</label>
          <select id="add-requirement" value={adding} onChange={(e) => setAdding(e.target.value)}>
            <option value="">{t("Choose a credential…")}</option>
            {types.map((ct) => (
              <option key={ct.id} value={ct.id}>
                {ct.name}
              </option>
            ))}
          </select>
          <button type="button" onClick={() => void add()} disabled={!adding}>
            {t("Add")}
          </button>
        </div>
      ) : null}
    </section>
  );
}

/**
 * THE CHECK PANEL — 09-... §V8.2, made into a question somebody is asked.
 *
 * The two fields are the shift START and the shift END, both required and both
 * labelled in words. A scheduler who is never asked when the work finishes
 * cannot notice that the licence runs out halfway through it, which is the
 * entire case this slice exists for.
 */
export function GateCheck() {
  const { apiBase, me } = useSession();
  const { locale, t, refusal } = useT();
  const partial = usePartialLoad();
  // Whose clock the preflight shift window is typed in. See system/clock.ts.
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const [roles, setRoles] = useState<RoleDef[]>([]);
  /**
   * V31a. THE PANEL LOADS ITS OWN PEOPLE, and that is why it is now on a screen.
   *
   * It took a `workers` prop and **nothing anywhere passed it** — `GateCheck` was
   * exported, complete, and mounted by no screen at all. The gallery's own note
   * has described "the shift check that answers whether one person may take one
   * shift" since V8 and there was nothing behind the sentence. It already fetched
   * its own roles; fetching its own people is what let `elements.tsx` mount it
   * without inventing a parent to hold the state.
   */
  const [workerId, setWorkerId] = useState("");
  // S15: asked with what is typed, not one capped page (it asked for 200 and got 100).
  const searchWorkers = useCallback(
    async (q: string) => {
      const res = await send<Failable<{ workers: WorkerOption[] }>>(console_.listWorkers(apiBase, { q, limit: 20 }));
      return (res.body.workers ?? []).map((w) => ({ id: w.id, label: w.display_name }));
    },
    [apiBase],
  );
  const [roleDefId, setRoleDefId] = useState("");
  const [startsAt, setStartsAt] = useState(localDateTimeInput(24));
  const [endsAt, setEndsAt] = useState(localDateTimeInput(32));
  /**
   * V31a. Blank means "nobody has said", which is what the database stores and
   * is a different answer from zero. See the note beside the field below.
   */
  const [hoursWorked, setHoursWorked] = useState("");
  const [result, setResult] = useState<GateResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void send<Failable<{ role_defs: RoleDef[] }>>(console_.listRoleDefs(apiBase)).then(({ status, body }) => {
      if (status === 200 && body.role_defs) setRoles(body.role_defs);
      else partial.note();
    }).catch(() => partial.note());
  }, [apiBase]);

  async function check() {
    setError(null);
    setResult(null);
    const { status, body } = await send<Failable<GateResult>>(
      console_.assignmentGate(apiBase, {
        workerId,
        roleDefId,
        startsAt: new Date(startsAt).toISOString(),
        endsAt: new Date(endsAt).toISOString(),
        hoursWorked: hoursWorked === "" ? null : Number(hoursWorked),
      }),
    );
    if (status === 200 && body.requirements) setResult(body as GateResult);
    else setError(refusal(body.error) ?? t("We could not check that shift."));
  }

  const ready = workerId !== "" && roleDefId !== "" && startsAt !== "" && endsAt !== "";

  return (
    <section className="panel">
      {partial.notice}
      <h2>{t("Can this person work this shift?")}</h2>
      <p className="muted">
        {t("Credentials are checked against the moment the shift ends, not against today. A licence that runs out partway through a shift does not cover it.")}
      </p>

      <SearchSelect
        id="gate-worker"
        label={t("Person")}
        value={workerId}
        onChange={setWorkerId}
        search={searchWorkers}
        placeholder={t("Type a name or reference")}
      />
      <div className="field">
        <label htmlFor="gate-role">{t("Role")}</label>
        <select id="gate-role" value={roleDefId} onChange={(e) => setRoleDefId(e.target.value)}>
          <option value="">{t("Choose…")}</option>
          {roles.map((r) => (
            <option key={r.id} value={r.id}>
              {translateLabel(locale, r.name)}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor="gate-start">{t("Shift starts")}</label>
        <input
          id="gate-start"
          type="datetime-local"
          value={startsAt}
          onChange={(e) => setStartsAt(e.target.value)}
          required
        />
      </div>
      <div className="field">
        <label htmlFor="gate-end">{t("Shift ends")}</label>
        <input
          id="gate-end"
          type="datetime-local"
          value={endsAt}
          onChange={(e) => setEndsAt(e.target.value)}
          required
        />
      </div>
      {/* ★ WHOSE CLOCK. Both fields are datetime-local and carry no zone. This
          preflight decides whether a person may be BOOKED on that window, so a
          silent hour shift moves the answer. See system/clock.ts. */}
      <p className="field-hint">{typedTimeHint(orgZone, t)}</p>
      {/* V31a. THE FIELD THE NIGHT SHIFT NEEDS.
          A shift running 20:00 to 04:00 crosses two dates, and until somebody
          says how many hours it is the daily ceiling cannot measure either of
          them — so it stays quiet rather than refusing on a number nobody
          computed. Filling this in is what lets it answer.

          LEFT BLANK BY DEFAULT AND NEVER PREFILLED FROM THE TIMES. The elapsed
          span is not the worked hours: a twelve-hour static guarding window with
          three hours of stand-down in it is nine, and a console that guessed
          would put a figure nobody measured in front of a legal ceiling. */}
      <div className="field">
        <label htmlFor="gate-hours">{t("Hours actually worked (optional)")}</label>
        <input
          id="gate-hours"
          type="number"
          min="0.25"
          max="24"
          step="0.25"
          value={hoursWorked}
          placeholder={t("Leave blank if you do not know")}
          onChange={(e) => setHoursWorked(e.target.value)}
        />
        <p className="field-hint">
          {t("Only needed when a shift runs past midnight. Without it we cannot tell how many of these hours fall on which day, so the daily limit is not checked at all — and we say so rather than guessing.")}
        </p>
      </div>

      <button type="button" onClick={() => void check()} disabled={!ready}>
        {t("Check")}
      </button>

      {error ? <StatusChip tone="failed" label={error} /> : null}

      {result ? (
        <div>
          {/* V15. THE VERDICT READS `refused`, NOT `blocked`.
              `blocked` is V8's word and still means only the credentials; a
              scheduler asking "can I book this person" is asking about all four
              conflict classes, and a screen answering "Can be assigned" over a
              double booking would be answering a narrower question than the one
              being asked. */}
          <p>
            <StatusChip
              tone={result.refused ? "failed" : result.warnings.length > 0 ? "attention" : "live"}
              label={
                result.refused
                  ? t("Cannot be assigned")
                  : result.warnings.length > 0
                    ? t("Can be assigned, with warnings")
                    : t("Can be assigned")
              }
            />
          </p>
          <p>{gateSummary(result, t)}</p>

          {/* THE CONFLICTS THAT ARE NOT CREDENTIALS. The credential rows are
              rendered verdict by verdict in the table below, with a chip and an
              expiry date each; repeating them here would show one refusal twice
              and leave a reader wondering whether it was two problems. */}
          {conflictSummary(result) ? (
            <>
              {/* V31a. THE SUMMARY IS FOR A LIST, NOT FOR ONE ROW.
                  `conflictSummary` deliberately returns the sentence itself when
                  there is exactly one conflict — and the list below already
                  renders that sentence, with its chip. Printed both ways it read
                  as two problems. Nothing could see it until this slice put the
                  panel on a screen. */}
              {result.conflicts.filter((c) => !c.repeats_the_gate).length > 1 ? (
                <p>{conflictSummary(result, t)}</p>
              ) : null}
              <ul className="gate-conflicts">
                {result.conflicts
                  .filter((c) => !c.repeats_the_gate)
                  .map((c, i) => {
                    const chip = conflictTone(c);
                    return (
                      <li key={`${c.kind}/${i}`}>
                        <StatusChip tone={chip.tone} label={translateLabel(locale, chip.label)} />
                        {c.check_kind_label ? <span className="muted">{c.check_kind_label}:</span> : null}
                        <span>{c.explanation}</span>
                      </li>
                    );
                  })}
              </ul>
            </>
          ) : null}
          {result.requirements.length > 0 ? (
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">{t("Credential")}</th>
                  <th scope="col">{t("Verdict")}</th>
                  <th scope="col">{t("Why")}</th>
                </tr>
              </thead>
              <tbody>
                {result.requirements.map((v) => {
                  const chip = gateTone(v);
                  return (
                    <tr key={v.credential_type_id}>
                      <td>{v.credential_name}</td>
                      <td>
                        <StatusChip tone={chip.tone} label={translateLabel(locale, chip.label)} />
                      </td>
                      {/* THE SERVER'S SENTENCE, VERBATIM. Absolute rule 6, and
                          the only copy of the rule that decided it. */}
                      <td>{v.explanation}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
